<script setup lang="ts">
import AppButton from '~/components/AppButton.vue'
import type { CmsCustomComponentType } from '~/layers/cms/app/types/cmsTypes'
import { nextTick, onMounted, onBeforeUnmount, useTemplateRef } from 'vue'
import type { Map as LeafletMap, MapOptions } from 'leaflet'

interface GestureMapOptions extends MapOptions {
  gestureHandling?: boolean
}

const mapContainer = useTemplateRef<HTMLElement>('mapContainer')
let map: LeafletMap | null = null

onMounted(async () => {
  await nextTick()
  const L = await import('leaflet')
  await import('leaflet/dist/leaflet.css')

  if (!mapContainer.value) return

  const zoom = /Mobi|Android/i.test(navigator.userAgent) ? 16 : 17

  map = L.map(mapContainer.value, {
    gestureHandling: true
  } as GestureMapOptions).setView([51.80664364980329, 19.32824148108245], zoom)

  const resizeObserver = new ResizeObserver(() => {
    map?.invalidateSize()
  })
  resizeObserver.observe(mapContainer.value)

  map.scrollWheelZoom.disable()
  map.on('focus', () => {
    map?.scrollWheelZoom.enable()
  })
  map.on('blur', () => {
    map?.scrollWheelZoom.disable()
  })

  L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png').addTo(map)

  const znaczniki: Record<string, { iconSize: [number, number], location: [number, number], icon: string }> = {
    'Ostoja seniora': {
      iconSize: [64, 64],
      location: [51.80664364980329, 19.32824148108245],
      icon: 'https://api.ostoja-seniora.pl/files/mapa-znacznik-ostoja.svg'
    },
    'Parking': {
      iconSize: [64, 64],
      location: [51.80753660610626, 19.327648928527054],
      icon: 'https://api.ostoja-seniora.pl/files/mapa-znacznik-parking.svg'
    }
  }

  Object.entries(znaczniki).forEach(([name, { iconSize, location, icon }]) => {
    const markerIcon = L.divIcon({
      html: `<div class="bob-wrapper"><img src="${icon}" style="width:${iconSize[0]}px;height:${iconSize[1]}px" alt="${name}"></div>`,
      className: '',
      iconSize
    })

    L.marker(location, { icon: markerIcon, title: name, alt: name })
      .addTo(map!)
      .on('click', () => {
        const [lat, lng] = location
        window.open(
          `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`,
          '_blank',
          'noopener,noreferrer'
        )
      })
  })
})

onBeforeUnmount(() => {
  if (map) {
    map.remove()
    map = null
  }
})

const props = defineProps<{
  cmsDataPath: (string | number)[]
}>()
const nazwa_komponentu = 'Contact/Map2'
const component_default_values: CmsCustomComponentType = {
  id: { value: undefined, default_value: 'Lorem Ipsum', settingType: 'text', setting_label: 'zmien id' }
}
</script>

<template>
  <CmsCustomComponent
    v-slot="{ component_values }"
    :cms-data-path="[...props.cmsDataPath]"
    :component-name="nazwa_komponentu"
    :defaults="component_default_values"
    :open-options="true"
  >
    <div
      :id="component_values?.id?.value"
      class="xl:px-xl pb-10 pt-0 xl:py-20"
    >
      <div class="pt-10 pb-10 xl:pb-24 px-xl xl:px-20 flex flex-col gap-12 bg-background-2 rounded-3xl">
        <div class="flex flex-col xl:flex-row justify-between gap-7.5 xl:items-center">
          <div class="flex flex-col gap-7.5 min-w-1/3">
            <span class="text-Tekst text-H2">
              <CmsText
                :cms-data-path="[...props.cmsDataPath, 'component_data', 'text_1']"
              />
            </span>
            <span class="text-H4 text-Tekst">
              <CmsText
                :cms-data-path="[...props.cmsDataPath, 'component_data', 'text_2']"
              />
            </span>
          </div>
          <div class="flex flex-col gap-7.5">
            <span class="text-H4 text-Tekst">
              <CmsText
                :cms-data-path="[...props.cmsDataPath, 'component_data', 'text_3']"
              />
            </span>
            <span class="text-H4 text-Tekst ">
              <CmsText
                :cms-data-path="[...props.cmsDataPath, 'component_data', 'text_4']"
              />
            </span>
            <CmsCustomComponentList
              v-slot="{ component_values: componentListValues }"
              :cms-data-path="[...props.cmsDataPath, 'component_data', 'list']"
              :open-options="true"
              component-name="images_list"
              class="min-w-5 min-h-5 flex flex-row flex-wrap gap-7.5"
            >
              <div
                v-for="(item, index) in componentListValues"
                :key="JSON.stringify(item)"
                class="flex flex-row flex-wrap gap-7.5"
              >
                <span class="text-Tekst hidden xl:block">
                  <CmsText
                    :cms-data-path="[...props.cmsDataPath, 'component_data', 'list', 'component_data', index, 'text']"
                  />
                </span>
                <div class="xl:hidden block">
                  <CmsLink
                    v-slot="{ link }"
                    :cms-data-path="[...props.cmsDataPath, 'component_data', 'list', 'component_data', index, 'link']"
                  >
                    <AppButton
                      button-theme="Secondary"
                      :href="link?.href"
                      :target="link?.target"
                    >
                      <span class="link-text">{{ link?.link_text ?? "Lorem ipsum" }}</span>
                    </AppButton>
                  </CmsLink>
                </div>
              </div>
            </CmsCustomComponentList>
          </div>
        </div>
        <div
          ref="mapContainer"
          class="w-full h-[40svh] xl:h-150 rounded-3xl"
        />
      </div>
    </div>
  </CmsCustomComponent>
</template>
